import { Injectable } from '@angular/core';
import { Audio } from '../models/audio';
import { PageService } from './page.service';
import { CrudService } from './crud.service';
import { ApplicationStateService } from './application-state.service';
import { AudioFactory } from '../models/factories/audio-factory';

@Injectable({
    providedIn: 'root'
})
export class BookAudioService {

    private audios: Array<Audio>;
    private indexToAudioMap: Map<number, Array<Audio>> = new Map();

    constructor(private applicationStateService: ApplicationStateService,
                private factory: AudioFactory,
                private pageService: PageService,
                private crudService: CrudService) {
    }

    setAudios(audios: Array<Audio>): void {
        this.audios = [];
        this.indexToAudioMap = new Map();
        audios.forEach(entity => this.includeToIndexMap(entity));
    }

    /**
     * Similar to setAudios(audios) but in addition the passed values are transformed in forehand.
     * This method purpose is to do the transformation and the storing in just one for-Loop.
     * @param objects
     */
    setObjectsToAudios(objects: Array<any>): void {
        this.audios = [];
        this.indexToAudioMap = new Map();
        objects.forEach(entity => {
            this.includeToIndexMap(this.factory.buildFromObject(entity));
        });
    }

    getAudios(): Promise<Array<Audio>> {

        return new Promise(resolve => {

            const thiz = this;

            if (!thiz.applicationStateService.bookConfig.bookPermissions.bookAudio.view) {
                thiz.audios = [];
            }

            if (thiz.audios) {
                resolve(thiz.audios);

                return;
            }

            thiz.crudService.getAllEntitiesOfBook(Audio, this.factory)
                .then(audios => {
                    this.setAudios(audios as Array<Audio>);
                    resolve(audios as Array<Audio>);
                });
        });
    }

    getAudiosOfPage(index: number): Promise<Array<Audio>> {

        return new Promise(resolve => {
            this.getAudios()
                .then(() => {
                    if (this.indexToAudioMap.get(index)) {
                        resolve(this.indexToAudioMap.get(index));
                    } else {
                        resolve([]);
                    }
                });
        });
    }

    getAudiosOfPages(pageIndex: number): Promise<Array<Audio>> {

        return new Promise<Array<Audio>>(resolve => {

            let audios = [];
            const promises = [];
            // Add the the promises of all search pages to the array
            for (let runningPageIndex = 0;
                 runningPageIndex < this.applicationStateService.currentViewMode.pagesToShow;
                 runningPageIndex++) {
                promises.push(this.getAudiosOfPage(pageIndex + runningPageIndex));
            }

            Promise.all(promises)
                .then(values => {
                    values.forEach(value => audios = audios.concat(value));
                    resolve(audios);

                });
        });
    }

    createAudio(audio: Audio): Promise<Audio> {
        return this.crudService.create(audio) as Promise<Audio>;
    }

    updateAudio(audio: Audio): Promise<Audio> {
        return this.crudService.update(audio) as Promise<Audio>;
    }

    deleteAudio(audio: Audio): Promise<boolean> {
        return this.crudService.remove(audio) as Promise<boolean>;
    }

    logUsage(audio: Audio): Promise<boolean> {
        return this.crudService.log(audio) as Promise<boolean>;
    }

    private includeToIndexMap(audio: Audio): void {
        const indexOfAudio = this.pageService.getIndexByPageId(audio.pageId);
        audio.index = indexOfAudio;
        this.audios.push(audio);

        if (!this.indexToAudioMap.get(indexOfAudio)) {
            this.indexToAudioMap.set(indexOfAudio, [audio]);
        } else {
            this.indexToAudioMap.get(indexOfAudio)
                .push(audio);
        }
    }
}
